import {
  LoadingOutlined,
  PlayCircleFilled,
  WarningOutlined,
} from "@ant-design/icons";
import { useMutation, useSubscription } from "@apollo/client";
import { WebRTCPlayer } from "@eyevinn/webrtc-player";
import * as Sentry from "@sentry/react";
import { Alert, Spin } from "antd";
import { useEffect, useRef, useState, useCallback } from "react";

import { HEARTBEAT_SEND_INTERVAL } from "./utils";
import {
  LiveStreamAvailability,
  LiveStreamAvailabilityDocument,
  LiveStreamAvailabilityStatus,
  LiveStreamHeartbeatDocument,
} from "../../graphql/graphql";

interface LiveStreamPlayerProps {
  playAllButtonClicked: boolean;
  stream: {
    cameraConfigId: string;
    title: string;
    urlWebRTC: string;
    urlHls: string;
    username: string;
    password: string;
    cameraName: string;
  };
}

const LiveStreamPlayer = ({
  playAllButtonClicked,
  stream,
}: LiveStreamPlayerProps & { assetSlug: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<WebRTCPlayer>();
  const [liveStreamAvailabilityStatus, setLiveStreamAvailabilityStatus] =
    useState<LiveStreamAvailabilityStatus | undefined>(undefined);
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [streamError, setStreamError] = useState(false);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [mutation] = useMutation(LiveStreamHeartbeatDocument);

  const { data: subscriptionData } = useSubscription<{
    liveStreamAvailability: LiveStreamAvailability;
  }>(LiveStreamAvailabilityDocument, {
    variables: { cameraConfigId: stream.cameraConfigId },
    skip: !isLoading,
  });

  const mutate = useCallback(async () => {
    await mutation({
      variables: { cameraConfigId: stream.cameraConfigId },
    });
  }, [mutation, stream.cameraConfigId]);

  useEffect(() => {
    if (videoRef.current === null) {
      console.error("Video element ref is null");
    } else {
      playerRef.current = new WebRTCPlayer({
        video: videoRef.current,
        type: "whep",
        statsTypeFilter: "^candidate-*|^inbound-rtp",
      });
    }

    // Stop the heartbeat when component unmounts
    return () => {
      stopHeartbeat();
    };
  }, []);

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isActive) {
      heartbeatIntervalRef.current = setInterval(() => {
        mutate().catch((error: unknown) => Sentry.captureException(error));
      }, HEARTBEAT_SEND_INTERVAL);
    } else {
      stopHeartbeat();
    }
  }, [isActive, mutate]);

  const onPlayClicked = () => {
    setIsActive(true);
    setIsLoading(true);
  };

  const onStopClicked = () => {
    setIsActive(false);
    setIsLoading(false);
  };

  const playIcon = (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1,
      }}
    >
      <PlayCircleFilled
        style={{ fontSize: "38px", color: "#fff" }}
        onClick={() => {
          onPlayClicked();
        }}
      />
    </div>
  );

  const loadingSpinner = (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1,
      }}
    >
      <Spin
        indicator={
          <LoadingOutlined style={{ fontSize: 38, color: "#fff" }} spin />
        }
      />
    </div>
  );

  const errorMessage = (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        zIndex: 1,
        width: "80%",
      }}
    >
      <Alert
        message="Stream Unavailable"
        description="The stream is not available at the moment. Please try again later."
        type="error"
        showIcon
        icon={<WarningOutlined />}
      />
    </div>
  );

  const videoPlayer = (
    <video
      ref={videoRef}
      autoPlay
      controls
      playsInline
      onPause={onStopClicked}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  );

  useEffect(
    () => {
      if (playAllButtonClicked) {
        onPlayClicked();
      }
    },
    // onPlayClicked does not need to be listed as a dependency here, because it is defined
    // in this function and will "change" on every render.
    [playAllButtonClicked],
  );

  useEffect(() => {
    if (subscriptionData !== undefined) {
      setLiveStreamAvailabilityStatus(
        subscriptionData.liveStreamAvailability.state,
      );
    }
  }, [subscriptionData]);

  useEffect(() => {
    const startStream = async () => {
      try {
        const authKey = btoa(`${stream.username}:${stream.password}`);
        const basicAuthKey = `Basic ${authKey}`;
        await playerRef.current?.load(new URL(stream.urlWebRTC), basicAuthKey);
      } catch (error) {
        console.error("Error starting stream:", error);
        setIsActive(false);
        setStreamError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (liveStreamAvailabilityStatus !== undefined) {
      switch (liveStreamAvailabilityStatus) {
        case LiveStreamAvailabilityStatus.Success:
          // Show/start the live stream
          startStream().catch((error: unknown) => {
            console.error("Error starting stream:", error);
          });
          break;
        case LiveStreamAvailabilityStatus.Failure:
          // Keep checking the status until we reach a terminal state
          break;
        case LiveStreamAvailabilityStatus.Timeout:
          // Live stream not available
          setIsActive(false);
          setIsLoading(false);
          setStreamError(true);
      }
    }
  }, [
    liveStreamAvailabilityStatus,
    stream.urlWebRTC,
    stream.username,
    stream.password,
  ]);

  return (
    <div
      style={{
        flex: 1,
        borderRadius: "20px",
        overflow: "hidden",
        maxWidth: "1000px",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          /* 16:9 aspect ratio */
          paddingTop: "56.25%",
        }}
      >
        {videoPlayer}
        {!isActive && !isLoading && playIcon}
        {isLoading && loadingSpinner}
        {streamError && errorMessage}
      </div>
    </div>
  );
};

export default LiveStreamPlayer;
